/**
 * useCloseMobileNav.ts
 *
 * Dismisses the mobile left-nav drawer once a nav item has opened a screen.
 *
 * Below `md` the left nav renders as a Radix Sheet laid over the page, and
 * nothing in the app ever called `setOpenMobile` — so the selected screen loaded
 * behind a panel the user then had to dismiss by hand. Callers attach the
 * returned callback to the surfaces that open a final screen; section headers,
 * which also expand a submenu, deliberately leave the drawer open.
 *
 * Above `md` this does nothing at all. `isMobile` is false, and `openMobile` is
 * read only by the sidebar's mobile branch, so desktop rendering, the collapse
 * chevron and the persisted `nh:sidebar:nav-open` state are untouched. The
 * `forceDesktopLayout` kill switch pins `isMobile` false and therefore disables
 * this too.
 *
 * Only valid inside the app shell's `SidebarProvider` (`components/layout/Layout`);
 * `useSidebar` throws without one, by design.
 */
import { useCallback } from 'react';
import { useSidebar } from '@/components/ui/sidebar';

export function useCloseMobileNav(): () => void {
  const { isMobile, setOpenMobile } = useSidebar();

  return useCallback(() => {
    if (isMobile) setOpenMobile(false);
  }, [isMobile, setOpenMobile]);
}
